/**
 * `GET/PUT venue/v1/manager/venues/{venueId}/courts/{courtId}/operating-hours` 계약 사본(#600 §1·§2).
 * `courtTypes.ts` 와 같은 규칙 — 서버가 계약을 바꾸지 않는 한 필드를 파생·개명하지 않는다.
 */

export interface CourtOperatingDaySetting {
  dayOfWeek: string
  dayOfWeekLabel: string
  active: boolean
  /** "HH:mm". `active=false` 면 null. */
  startTime: string | null
  /** "HH:mm". 자정 마감은 "24:00". `active=false` 면 null. */
  endTime: string | null
}

export interface CourtOperatingHourView {
  baseTime: string
  venueId: string
  courtId: number
  courtName: string
  status: 'OPEN' | 'SUSPENDED' | 'MAINTENANCE'
  statusLabel: string
  /** 지금 저장할 수 있는가 = `PUT` 이 통과하는 조건과 같은 판정. */
  editable: boolean
  editBlockReason: string | null
  editBlockNotice: string | null
  reservationUnitMinutes: number
  reservationUnitLabel: string
  /** 🔒 선택지도 서버가 준다 — 하드코딩 금지(#600 §1). */
  allowedReservationUnitMinutes: number[]
  maxReservationMinutes: number
  maxReservationLabel: string
  /** "요일 동일" 여부(파생값) — 저장하지 않는다. */
  sameForAllDays: boolean
  operatingStartTime: string | null
  operatingEndTime: string | null
  /** 🚨 항상 7행, 월→일 순서 고정. */
  days: CourtOperatingDaySetting[]
  upcomingReservationCount: number
  /** `editable && upcomingReservationCount == 0` — 예약 단위 필드 전용 잠금. */
  unitChangeable: boolean
  blockNotice: string | null
  updatedAt: string | null
}

/** `PUT` 요청 바디(#600 §2 — 전체 치환). 목록에 없는 요일은 미운영으로 저장된다. */
export interface CourtOperatingHourSaveInput {
  reservationUnitMinutes: number
  maxReservationMinutes: number
  days: { dayOfWeek: string; active: boolean; startTime?: string; endTime?: string }[]
}

/** #600 §1 — forward-only, 월→일 고정 순서. */
export const DAY_OF_WEEK_ORDER = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const

/**
 * "00:00" ~ "24:00", 30분 간격(49개). 종료 select 는 "24:00" 자정 마감 선택이 필요해 이 목록을 쓰고,
 * 시작 select 도 같은 목록을 재사용한다 — "24:00" 을 시작으로 골라도 "종료 > 시작" 검증이 자연히 막는다.
 */
export const TIME_OF_DAY_OPTIONS: string[] = Array.from({ length: 49 }, (_, index) => {
  const totalMinutes = index * 30
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
})
